package com.iregained.eventapp.components.sections.event_details.sections

import androidx.compose.runtime.*
import com.iregained.eventapp.components.sections.event_details.styles.SocialProofLogoStyle
import com.iregained.eventapp.components.widgets.SectionHeader
import com.iregained.eventapp.theme.toSitePalette
import com.iregained.eventapp.theme.unextractable
import com.iregained.eventapp.utils.LocalBreakpoint
import com.iregained.eventapp.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

@Composable
private fun Modifier.staggeredPopIn(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 0.5f).transition(
        Transition.of(
            "opacity", duration = 0.6.s, timingFunction = TransitionTimingFunction.EaseOut, delay = delayMs.ms
        ), Transition.of(
            "transform", duration = 0.6.s, timingFunction = TransitionTimingFunction.EaseOut, delay = delayMs.ms
        )
    )
}

@Composable
fun EventSocialProofSection(data: EventSocialProofSectionData = defaultEventSocialProofSectionData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "home-accreditation-section"
    val palette = ColorMode.current.toSitePalette()

    var isVisible by remember { mutableStateOf(false) }

    if (data.accreditations.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.custom.gray)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(attrs = Modifier.fillMaxWidth().toAttrs {
                ref { element ->
                    val observer = IntersectionObserver { entries, obs ->
                        entries.forEach {
                            if (it.isIntersecting) {
                                isVisible = true; obs.unobserve(element)
                            }
                        }
                    }
                    observer.observe(element)
                    onDispose { observer.disconnect() }
                }
            }) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    if (data.sectionHeader.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionHeader,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 25.px).staggeredPopIn(isVisible, 0),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    Row(
                        modifier = Modifier.fillMaxWidth().maxWidth(1440.px).display(DisplayStyle.Flex)
                            .flexWrap(FlexWrap.Wrap).justifyContent(JustifyContent.Center).alignItems(AlignItems.Center)
                    ) {
                        data.accreditations.forEachIndexed { index, accData ->
                            key(accData.src) {
                                // RESOLVED: Passing the entire accData object instead of just the src string
                                ClinicalAccreditationLogo(
                                    data = accData,
                                    modifier = Modifier.weight(1f).staggeredPopIn(isVisible, 200 + (index * 150))
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

// RESOLVED: Updated to accept AccreditationData and render the region text below the image
@Composable
private fun ClinicalAccreditationLogo(data: AccreditationData, modifier: Modifier = Modifier) {
    Column(
        modifier = modifier.minWidth(250.px).padding(10.px), horizontalAlignment = Alignment.CenterHorizontally
    ) {
        if (data.src.isNotBlank()) {
            Image(src = data.src, modifier = SocialProofLogoStyle.toModifier().unextractable())
        }
        SpanText(
            text = data.regionName,
            modifier = Modifier.margin(top = 8.px).fontSize(20.px).fontWeight(FontWeight.Bold)
                .color(Colors.DarkSlateGray).textAlign(TextAlign.Center)
        )
    }
}

// CMS Data Model
/**
 * CMS IMAGE UPLOAD SPECS:
 * @field src:
 * - Aspect Ratio: Flexible (Horizontal/Landscape preferred).
 * - Recommended Dimensions: Max width 300px, max height 100px.
 * - Format: SVG or transparent PNG ONLY. No white backgrounds.
 * - Max File Size: 100KB.
 * - Behavior: Will scale to fit within a 300px wide container to ensure responsiveness.
 */
data class AccreditationData(val src: String, val regionName: String)

data class EventSocialProofSectionData(
    val sectionId: String? = null, val sectionHeader: String, val accreditations: List<AccreditationData>
)

val defaultEventSocialProofSectionData = EventSocialProofSectionData(
    sectionId = "home-accreditation-section",
    sectionHeader = "CLINICIAN TRUSTED AND REGULATORY APPROVED",
    accreditations = listOf(
        AccreditationData(Res.Images.LANDING.SOCIAL_PROOF.HEALTH_CANADA, "Canada"),
        AccreditationData(Res.Images.LANDING.SOCIAL_PROOF.FDA_APPROVED, "United States"),
        AccreditationData(Res.Images.LANDING.SOCIAL_PROOF.CDSCO, "India"),
        AccreditationData(Res.Images.LANDING.SOCIAL_PROOF.HSA, "Singapore")
    )
)